// types
import type { ThemeModeTypes } from "theme/types";
import type { ThemeOptions } from "@mui/material/styles";

// BRAND COLORS - Ultra-premium palette inspired by Linear/Vercel/Raycast
const PRIMARY_COLOR = {
  light: "#818CF8",
  main: "#4F46E5",
  dark: "#3730A3",
  contrastText: "#ffffff",
};
const SECONDARY_COLOR = {
  light: "#A78BFA",
  main: "#7C3AED",
  dark: "#5B21B6",
  contrastText: "#ffffff",
};

// UTIL COLORS
const SUCCESS_COLOR = {
  light: "#6EE7B7",
  main: "#059669",
  dark: "#047857",
  contrastText: "#fff",
};
const ERROR_COLOR = {
  light: "#FDA4AF",
  main: "#E11D48",
  dark: "#BE123C",
  contrastText: "#fff",
};
const WARNING_COLOR = {
  light: "#FCD34D",
  main: "#D97706",
  dark: "#B45309",
  contrastText: "#fff",
};
const INFO_COLOR = {
  light: "#7DD3FC",
  main: "#0284C7",
  dark: "#0369A1",
  contrastText: "#fff",
};

// NUTRITION COLORS
const COMMON_COLORS = {
  white: "#ffffff",
  black: "#0C0A09",
};

const GREY_COLOR = {
  50: "#FAFAF9",
  100: "#F5F5F4",
  200: "#E7E5E4",
  300: "#D6D3D1",
  400: "#A8A29E",
  500: "#78716C",
  600: "#57534E",
  700: "#44403C",
  800: "#292524",
  900: "#1C1917",
};

interface CreateGradientTypes {
  direction: "bottom" | "top" | "right" | "left";
  color: string;
}
function createGradient(
  color1: CreateGradientTypes["color"],
  color2: CreateGradientTypes["color"],
  direction?: CreateGradientTypes["direction"]
) {
  return `linear-gradient(to ${direction || "bottom"}, ${color1}, ${color2})`;
}

const GRADIENTS = {
  primary: createGradient(PRIMARY_COLOR.light, PRIMARY_COLOR.main),
  info: createGradient(INFO_COLOR.light, INFO_COLOR.main),
  success: createGradient(SUCCESS_COLOR.light, SUCCESS_COLOR.main),
  warning: createGradient(WARNING_COLOR.light, WARNING_COLOR.main),
  error: createGradient(ERROR_COLOR.light, ERROR_COLOR.main),
};

const DARK_PALETTE = {
  background: {
    paper: "#1A1523",
    default: "#110F1A",
    neutral: "#2D2640",
  },
  action: {
    active: "#E7E5E4",
    hover: "rgba(255,255,255,0.06)",
    selected: "rgba(255,255,255,0.12)",
    disabled: "rgba(255,255,255,0.3)",
    disabledBackground: "rgba(255,255,255,0.12)",
  },
  text: {
    primary: "#F5F5F4",
    secondary: "#A8A29E",
    disabled: "#57534E",
  },
  divider: "rgba(255,255,255,0.08)",
};
const LIGHT_PALETTE = {
  background: {
    paper: "#FFFFFF",
    default: "#FAFAF9",
    neutral: GREY_COLOR[100],
  },
  action: {
    active: "#57534E",
    hover: "rgba(0,0,0,0.04)",
    selected: "rgba(0,0,0,0.08)",
    disabled: "rgba(0,0,0,0.26)",
    disabledBackground: "rgba(0,0,0,0.12)",
  },
  text: {
    primary: "#1C1917",
    secondary: "#57534E",
    disabled: "#A8A29E",
  },
  divider: "rgba(0,0,0,0.06)",
};

interface PaletteOptions {
  mode: ThemeModeTypes;
  customColors?: {
    primary?: string;
    secondary?: string;
  };
}

// Helper to generate light/dark variants from a main color
function generateColorVariants(mainColor: string) {
  return {
    light: mainColor,
    main: mainColor,
    dark: mainColor,
    contrastText: "#ffffff",
  };
}

const Palette: (
  mode: PaletteOptions["mode"],
  customColors?: PaletteOptions["customColors"]
) => ThemeOptions["palette"] = (mode, customColors) => ({
  mode: mode || "light",
  primary: customColors?.primary
    ? generateColorVariants(customColors.primary)
    : PRIMARY_COLOR,
  secondary: customColors?.secondary
    ? generateColorVariants(customColors.secondary)
    : SECONDARY_COLOR,
  success: SUCCESS_COLOR,
  error: ERROR_COLOR,
  warning: WARNING_COLOR,
  info: INFO_COLOR,
  common: COMMON_COLORS,
  grey: GREY_COLOR,
  ...(mode === "light"
    ? {
        ...LIGHT_PALETTE,
      }
    : {
        ...DARK_PALETTE,
      }),
});

export { Palette, createGradient, GRADIENTS };
export type { PaletteOptions };
